import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material'
import { Error as ErrorIcon, CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon, CloudSync as CloudSyncIcon } from '@mui/icons-material'

import { SyncStatus } from '@/core/offline/types/database.types'
import { formatDate, getSyncStatusColor, getSyncStatusText } from './helpers'

type StatusOverviewCardsProps = {
  syncStatus: SyncStatus
}

export function StatusOverviewCards({ syncStatus }: StatusOverviewCardsProps) {
  const { t } = useTranslation()

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <OverviewCard>
          <CloudSyncIcon color={getSyncStatusColor(syncStatus)} fontSize="large" />
          <Box>
            <Typography variant="h6" color={getSyncStatusColor(syncStatus)}>
              {getSyncStatusText(syncStatus, t)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {syncStatus.lastSync && `${t('sync.lastSync')}: ${formatDate(syncStatus.lastSync)}`}
            </Typography>
          </Box>
        </OverviewCard>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <OverviewCard>
          <ScheduleIcon color="warning" fontSize="large" />
          <Box>
            <Typography variant="h4">{syncStatus.pendingCount}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('sync.pendingItems')}
            </Typography>
          </Box>
        </OverviewCard>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <OverviewCard>
          <ErrorIcon color="error" fontSize="large" />
          <Box>
            <Typography variant="h4">{syncStatus.failedCount}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('sync.failedItems')}
            </Typography>
          </Box>
        </OverviewCard>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <OverviewCard>
          <CheckCircleIcon color="success" fontSize="large" />
          <Box>
            <Typography variant="h4">{syncStatus.completedCount}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('sync.completedItems')}
            </Typography>
          </Box>
        </OverviewCard>
      </Grid>
    </Grid>
  )
}

function OverviewCard({ children }: { children: React.ReactNode }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          {children}
        </Stack>
      </CardContent>
    </Card>
  )
}
